import {
  api,
  canReadAudit,
  getAccessToken,
  type AircraftOverview,
  type ComponentLife,
  type ComponentsResponse,
  type DefectRow,
  type DirectiveRow,
  type DocumentRegister,
  type FlightRow,
  type ItemState,
  type ProgrammeResponse,
  type ReviewContext,
  type ReviewResponse,
  type Utilisation,
  type WeightBalance,
} from '../api';
import { callout } from '../dialog';
import { bindDocumentViewers, bindDownloads, docTitle, fmtBytes, removeDocument, reviseDocument, uploadDocument, verifyIntegrity } from '../dialogs/document';
import { completeReview, extendArc, recordExistingArc, recordSteps, startReview } from '../dialogs/review';
import { editComponent, fitComponent, removeComponent } from '../dialogs/component';
import { recordCompliance } from '../dialogs/ad-compliance';
import { addDirective } from '../dialogs/directive';
import { runDirectiveCheck } from '../dialogs/directive-check';
import { openPdfViewer } from '../dialogs/pdf-viewer';
import { bindModifications, modificationsCard } from './modifications';
import { bindStandardChanges, standardChangesTab } from './standard-changes';
import { endManagement } from '../dialogs/end-of-management';
import { editAircraft } from '../dialogs/edit-aircraft';
import { noteRegistration } from '../main';
import { defineStations, editProgrammeTask, recordProgrammeReview, recordWeighing, reviseProgramme } from '../dialogs/programme';
import { showRecordsTransfer } from '../dialogs/records-transfer';
import { raiseDefectFor } from '../dialogs/defect';
import {
  arcMeter,
  errorCard,
  esc,
  fmtDate,
  fmtDecimalHours,
  fmtHours,
  fmtInt,
  fmtMargin,
  html,
  loading,
  pill,
  REGIME_BASIS_LABEL,
  REGIME_LABEL,
  statusLabel,
  statusState,
  STRIPE,
  toast,
  type State,
} from '../ui';

export const TABS = [
  ['overview', 'Overview'],
  ['programme', 'Programme'],
  ['weight-balance', 'Weight &amp; balance'],
  ['directives', 'AD &amp; SB'],
  ['standard-changes', 'Modifications'],
  ['components', 'Components'],
  ['defects', 'Defects'],
  ['flights', 'Tech log'],
  ['documents', 'Documents'],
  ['review', 'Airworthiness review'],
] as const;

export type TabKey = (typeof TABS)[number][0];

const st = (s: ItemState | string): State =>
  s === 'OVERDUE' ? 'OVERDUE' : s === 'DUE_SOON' ? 'DUE_SOON' : 'OK';
const stateLabel = (s: ItemState | string): string =>
  s === 'OVERDUE' ? 'Overdue' : s === 'DUE_SOON' ? 'Due soon' : 'In tolerance';

const dueText = (r: { dueTsnHours: number | null; dueAt: string | null }): string =>
  r.dueTsnHours !== null ? `at ${fmtHours(r.dueTsnHours)} h` : fmtDate(r.dueAt);

// ── header ──────────────────────────────────────────────────────────────────

function placard(a: AircraftOverview, tab: TabKey): string {
  return html`
    <div class="placard">
      <div class="placard-top">
        <div class="idplate">
          <div class="lbl">Registration</div>
          <div class="r">${esc(a.registration)}</div>
        </div>
        <div style="flex:1 1 220px;min-width:0">
          <h1 style="font-size:19px">${esc(a.typeName)}</h1>
          <p class="tiny muted" style="margin-top:2px">
            S/N ${esc(a.serialNumber)} · ${esc(a.yearOfManufacture ?? '')} ·
            ${esc(a.baseAerodrome ?? '')} · ${esc(a.customer.name)}
          </p>
          ${/* The type certificate data sheet, and the ICAO designator beside
                it. Both were on file and neither was ever shown: the TCDS is
                what an authority asks for when the question is which standard
                this aeroplane was built to, and the designator is what the
                directive check matches on — worth being able to check without
                opening the type. */
            a.type.tcdsReference || a.type.designator
              ? html`<p class="tiny muted" style="margin-top:2px">
                  <a href="/types" title="The type record — shared by every aircraft of this model">
                    ${a.type.designator ? html`ICAO <span class="mono">${esc(a.type.designator)}</span>` : ''}
                    ${a.type.tcdsReference
                      ? html`${a.type.designator ? ' · ' : ''}TCDS <span class="mono">${esc(a.type.tcdsReference)}</span>`
                      : ''}
                    ${a.type.elaClass && a.type.elaClass !== 'NONE' ? ` · ${esc(a.type.elaClass)}` : ''}
                    ${a.type.isComplex ? ' · complex motor-powered' : ''}
                  </a>
                </p>`
              : ''}
          <div style="display:flex;gap:6px;margin-top:7px;flex-wrap:wrap">
            <span class="chip acc">${esc(REGIME_LABEL[a.regime] ?? a.regime)}</span>
            <span class="chip" title="${esc(a.regimeJustification ?? '')}">
              ${esc(REGIME_BASIS_LABEL[a.regimeBasis] ?? a.regimeBasis)}
            </span>
            ${a.approval ? html`<span class="chip">${esc(a.approval.reference)}</span>` : ''}
            ${a.arc
              ? html`<span class="chip">${esc(a.arc.formKind.replace('FORM_', 'Form ').toLowerCase())}</span>`
              : ''}
          </div>
        </div>
        <div style="text-align:right">
          ${pill(statusState(a.status), statusLabel(a.status))}
          <p class="tiny muted" style="margin-top:5px;max-width:34ch">
            ${esc(a.groundedReason ?? a.nextDue[0]?.title ?? 'Nothing due in the forecast window')}
          </p>
          ${a.archivedAt
            ? ''
            : html`<button class="btn pri sm" style="margin-top:8px"
                     data-dialog="work-pack" data-for-aircraft="${esc(a.id)}">Build work pack</button>`}
        </div>
      </div>
      <div class="tabs" style="align-items:center">
        ${TABS.map(
          ([key, label]) =>
            html`<button data-tab="${key}" class="${tab === key ? 'on' : ''}">${label}</button>`,
        ).join('')}
        <!--
          A link rather than a tab. The trail is not part of this aircraft's
          record — it is the record of what was done to it, and it lives on its
          own screen with its own filters and its own restriction on who may
          read it. Only the people the audit screen is open to see this at all,
          which is the same rule applied in the same place as the menu item.
        -->
        ${canReadAudit()
          ? html`<a class="tiny" style="margin-left:auto;padding:0 10px"
                   href="/audit?aircraft=${encodeURIComponent(a.id)}"
                   title="Everything recorded about ${esc(a.registration)}">Audit trail →</a>`
          : ''}
      </div>
    </div>
  `;
}

// ── overview ────────────────────────────────────────────────────────────────

/** Monthly flight hours. Single series, so the title names it and no legend. */
function utilisationChart(u: Utilisation): string {
  const data = u.series;
  if (data.length === 0) return html`<p class="muted tiny">No flights recorded yet.</p>`;

  const W = 560, H = 132, padL = 28, padB = 18, padT = 8;
  const max = Math.max(20, Math.ceil(Math.max(...data.map((d) => d.hours)) / 20) * 20);
  const iw = W - padL - 6, ih = H - padB - padT;
  const bw = iw / data.length;

  let grid = '', bars = '', labels = '';
  for (let g = 0; g <= max; g += max / 2) {
    const y = padT + ih - (g / max) * ih;
    grid += `<line class="gridline" x1="${padL}" y1="${y.toFixed(1)}" x2="${W - 6}" y2="${y.toFixed(1)}"/>`;
    labels += `<text class="axl" x="${padL - 6}" y="${(y + 3).toFixed(1)}" text-anchor="end">${g}</text>`;
  }
  data.forEach((d, i) => {
    const h = Math.max((d.hours / max) * ih, 2);
    const x = padL + i * bw + 1;              // 2px surface gap between bars
    const y = padT + ih - h;
    bars += `<rect class="bar${i === data.length - 1 ? ' last' : ''}" x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${(bw - 2).toFixed(1)}" height="${h.toFixed(1)}" rx="3" data-m="${d.month}" data-v="${d.hours}"/>`;
    const month = new Date(`${d.month}-01T00:00:00Z`).toLocaleString('en-GB', { month: 'short', timeZone: 'UTC' });
    labels += `<text class="axl" x="${(x + (bw - 2) / 2).toFixed(1)}" y="${H - 5}" text-anchor="middle">${month}</text>`;
  });

  return html`<div class="chart" data-unit=" h">
    <svg viewBox="0 0 ${W} ${H}" preserveAspectRatio="none" role="img"
         aria-label="Flight hours per month over the last year">${grid}${bars}${labels}</svg>
    <div class="tip"></div>
  </div>`;
}

function bindChart(root: HTMLElement): void {
  root.querySelectorAll<HTMLElement>('.chart').forEach((chart) => {
    const tip = chart.querySelector<HTMLElement>('.tip');
    if (!tip) return;
    const unit = chart.dataset.unit ?? '';
    chart.querySelectorAll<SVGElement>('.bar').forEach((bar) => {
      bar.addEventListener('mouseenter', () => {
        const r = bar.getBoundingClientRect(), cr = chart.getBoundingClientRect();
        tip.textContent = `${bar.dataset.m} · ${bar.dataset.v}${unit}`;
        tip.style.left = `${r.left - cr.left + r.width / 2}px`;
        tip.style.top = `${r.top - cr.top}px`;
        tip.classList.add('on');
      });
      bar.addEventListener('mouseleave', () => tip.classList.remove('on'));
    });
  });
}

function overviewTab(a: AircraftOverview, u: Utilisation): string {
  const engine = a.engines[0];
  const prop = a.propellers[0];
  const toTbo = engine?.limitHours != null ? engine.limitHours - engine.tsoHours : null;

  /**
   * What sits under a TSO figure.
   *
   * A component whose time since overhaul equals its time since new has never
   * been overhauled. One whose TSO is lower has been — and where no date is on
   * file for it, that is what the record is missing, not the overhaul. Saying
   * "never overhauled" against 1,211 h TSO on a 3,411 h engine would be
   * reading the blank date as a fact about the engine.
   */
  const overhaulNote = (c: ComponentLife): string =>
    c.lastOverhaulAt
      ? `overhauled ${esc(fmtDate(c.lastOverhaulAt))}`
      : c.tsoHours < c.tsnHours
        ? 'overhaul date not on file'
        : 'never overhauled';

  return html`
    <div class="cols">
      <div class="stack">
        <div class="card">
          <div class="card-h">
            <h2>Times &amp; lives</h2>
            <span class="lbl">as of ${esc(fmtDate(a.countersAsOf))}</span>
          </div>
          <div class="kv">
            <div><span class="lbl">Airframe TSN</span><b>${fmtHours(a.tsnHours)} h</b><small>${fmtInt(a.landingsTotal)} landings</small></div>
            ${engine
              ? html`<div><span class="lbl">Engine TSN</span><b>${fmtHours(engine.tsnHours)} h</b><small>${esc(engine.description)}</small></div>
                     <div><span class="lbl">Engine TSO</span><b>${fmtHours(engine.tsoHours)} h</b><small>${overhaulNote(engine)}</small></div>
                     <div><span class="lbl">To TBO</span><b>${toTbo !== null ? `${fmtHours(toTbo)} h` : '—'}</b><small>${engine.limitHours ? `limit ${fmtDecimalHours(engine.limitHours)} h` : 'on condition'}</small></div>`
              : ''}
            ${prop
              ? html`<div><span class="lbl">Propeller TSN</span><b>${fmtHours(prop.tsnHours)} h</b><small>${esc(prop.description)}</small></div>
                     <div><span class="lbl">Propeller TSO</span><b>${fmtHours(prop.tsoHours)} h</b><small>${overhaulNote(prop)}</small></div>`
              : ''}
            <div>
              <span class="lbl">${a.arc ? 'ARC expiry' : 'Airworthiness'}</span>
              <b>${a.arc ? esc(fmtDate(a.arc.expiresAt)) : '—'}</b>
              <small>${a.arc
                ? `${a.arc.extensionNumber} of 2 extensions used`
                : a.regime === 'FAA_91'
                  ? 'no ARC — FAA registry'
                  : 'no ARC on file'}</small>
            </div>
            <div>
              <span class="lbl">Last inspection</span>
              <b>${a.lastInspectionAt ? esc(fmtDate(a.lastInspectionAt)) : '—'}</b>
              <small>${a.lastInspectionAt
                ? esc([
                    a.lastInspectionTsn !== null ? `at ${fmtHours(a.lastInspectionTsn)} h` : null,
                    a.lastInspectionNote,
                  ].filter(Boolean).join(' · ') || 'recorded on the aircraft')
                : 'not recorded — Edit aircraft'}</small>
            </div>
            <div><span class="lbl">Utilisation</span><b>${fmtHours(u.totalHours)} h</b><small>12 months · ${fmtDecimalHours(u.averagePerMonth)} h/month</small></div>
          </div>
        </div>

        <div class="card">
          <div class="card-h"><h2>Flight hours per month</h2><span class="lbl">last 12 months</span></div>
          <div class="card-b">${utilisationChart(u)}</div>
          <div class="card-f">
            Forecast dates on the due list are projected from this rate. Current projection:
            <b class="mono">${a.avgHoursPerDay !== null ? `${a.avgHoursPerDay.toFixed(2)} h/day` : 'not enough data'}</b>.
          </div>
        </div>

        <div class="card">
          <div class="card-h"><h2>Regulatory basis</h2></div>
          <div class="card-b" style="display:flex;flex-direction:column;gap:10px">
            <div>
              <span class="lbl">Why this regime</span>
              <p style="margin-top:3px">
                <b>${esc(REGIME_BASIS_LABEL[a.regimeBasis] ?? a.regimeBasis)}</b>
                ${a.approval ? html` — managed under <b class="mono">${esc(a.approval.reference)}</b>` : ''}
              </p>
              <p class="tiny muted" style="margin-top:4px;line-height:1.5">${esc(a.regimeJustification ?? '')}</p>
            </div>
            ${a.programme
              ? html`<div>
                  <span class="lbl">Programme basis</span>
                  <p style="margin-top:3px">${esc(PROGRAMME_BASIS[a.programme.basis] ?? a.programme.basis)} · ${esc(a.programme.revision)}</p>
                  <p class="tiny muted" style="margin-top:3px">
                    Effective ${esc(fmtDate(a.programme.effectiveFrom))}${a.programme.nextReviewDue ? ` · annual review due ${fmtDate(a.programme.nextReviewDue)}` : ''}
                  </p>
                </div>`
              : ''}
          </div>
        </div>
      </div>

      <div class="stack">
        <div class="card">
          <div class="card-h"><h2>Next events</h2></div>
          <div class="rows">
            ${a.nextDue.length === 0
              ? html`<div class="row"><div class="bd"><div class="t">Nothing due in the forecast window</div></div></div>`
              : a.nextDue
                  .map(
                    (d) => html`<div class="row" style="border-left-color:var(--${d.state === 'OVERDUE' ? 'exceed' : d.state === 'DUE_SOON' ? 'caution' : 'ok'})">
                      <div class="bd">
                        <div class="t">${esc(d.title)}</div>
                        <div class="m">
                          ${esc(dueText(d))} · ${esc(fmtMargin(d.remaining, d.drivingUnit))}
                          ${/* A part running out and an inspection falling due
                                read alike here, and they are acted on in
                                different places — the Components tab, not the
                                programme — so the row says which it is. */
                            d.kind === 'COMPONENT'
                              ? html` · component life${d.code ? ` · ${esc(d.code)}` : ''}`
                              : ''}
                        </div>
                      </div>
                    </div>`,
                  )
                  .join('')}
          </div>
        </div>

        <div class="card">
          <div class="card-h"><h2>Record set</h2></div>
          <div class="rows">
            <div class="row">
              <div class="bd">
                <div class="t">Continuing airworthiness records</div>
                <div class="m">${a.regime === 'FAA_91' ? 'FAA — maintenance records per 91.417' : 'Retained per ML.A.305(h) · detailed records 36 months after release'}</div>
              </div>
              ${pill('OK', 'Complete')}
            </div>
            <div class="row">
              <div class="bd">
                <div class="t">Owner contract</div>
                <div class="m">${a.contract ? `${esc(a.contract.reference)} · valid to ${fmtDate(a.contract.validTo)}` : 'none on file'}</div>
              </div>
              ${a.contract ? pill('OK', 'Valid') : pill('NA', 'Missing')}
            </div>
            <div class="row">
              <div class="bd">
                <div class="t">Records transfer package</div>
                <div class="m">One-click export on sale — status, AMP, ADs, components, CRS history</div>
              </div>
              <button class="btn sm" data-act="records-transfer">Build</button>
            </div>
            <div class="row">
              <div class="bd">
                <div class="t">Aircraft record</div>
                <div class="m">Base and year change freely; registration, serial, owner and counters are corrections</div>
              </div>
              <button class="btn sm" data-act="edit-aircraft">Edit</button>
            </div>
            <div class="row">
              <div class="bd">
                <div class="t">${a.archivedAt ? 'Management ended' : 'End of management'}</div>
                <div class="m">
                  ${a.archivedAt
                    ? `Off the fleet since ${fmtDate(a.archivedAt)} — the record is retained`
                    : 'Takes the aircraft off the fleet and keeps its record, as ML.A.305(h) requires'}
                </div>
              </div>
              ${a.archivedAt
                ? html`<button class="btn sm" data-act="restore-aircraft">Resume management</button>`
                : html`<button class="btn sm" data-act="end-management">End management</button>`}
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-h"><h2>Certificates</h2></div>
          <div class="rows">
            ${a.certificates
              .map(
                (c) => html`<div class="row">
                  <div class="bd">
                    <div class="t">${esc(CERT_LABEL[c.kind] ?? c.kind)}</div>
                    <div class="m">
                      ${esc(c.reference ?? '')}${c.expiresAt ? ` · valid to ${fmtDate(c.expiresAt)}` : c.issuedAt ? ` · issued ${fmtDate(c.issuedAt)}` : ''}
                    </div>
                  </div>
                </div>`,
              )
              .join('')}
          </div>
        </div>
      </div>
    </div>
  `;
}

const PROGRAMME_BASIS: Record<string, string> = {
  ML_APPROVED_BY_ORG: 'AMP approved by the managing organisation — ML.A.302(b)',
  ML_DECLARED: 'Owner-declared AMP — ML.A.302(b)(2)',
  ML_MIP: 'Minimum inspection programme — ML.A.302(d)',
  M_APPROVED: 'Approved AMP — M.A.302',
  FAA_91_409A: 'Annual + 100-hour — 14 CFR 91.409(a)',
  FAA_PROGRESSIVE: 'Progressive inspection — 14 CFR 91.409(d)',
};

const CERT_LABEL: Record<string, string> = {
  COFA: 'Certificate of Airworthiness',
  NOISE: 'Noise certificate',
  RADIO_LICENCE: 'Radio station licence',
  INSURANCE: 'Insurance — EC 785/2004',
  REGISTRATION: 'Certificate of registration',
  PERMIT_TO_FLY: 'Permit to fly',
};

const SOURCE_LABEL: Record<string, string> = {
  MM_CHAPTER_5: 'MM Ch. 5',
  MPD: 'MPD',
  SB: 'Service bulletin',
  MIP: 'MIP',
  AD_DERIVED: 'Airworthiness directive',
  CMM: 'CMM',
  OPERATOR: 'Operator',
};

// ── programme ───────────────────────────────────────────────────────────────

function programmeTab(p: ProgrammeResponse): string {
  // Offered whether or not there is a programme: with none it is the way to
  // get one, and with one it is the way to rebuild from a newer revision of
  // the manual.
  const fromManuals = html`
    <button class="btn sm" data-act="amp-from-manuals">Build from the manuals</button>
  `;

  if (!p.programme) {
    return html`<div class="card">
      <div class="card-h">
        <div>
          <h2>No maintenance programme</h2>
          <p class="tiny dim">Nothing is scheduled on this aircraft until there is one</p>
        </div>
        ${fromManuals}
      </div>
      <div class="card-b muted">
        A programme can be typed in task by task, or read out of the aircraft’s own maintenance
        manuals and reviewed — whichever is less work. Reading them produces a proposal; it is
        approved by somebody holding AMP_APPROVE, never by the reading itself.
      </div>
    </div>`;
  }

  // Whether anything on this programme has ever been signed off here. A
  // programme read out of the manuals starts with none, and what the due dates
  // are measured from is then a question the screen has to answer rather than
  // leave the reader to work out from a column of dashes.
  const m = p.measuredFrom;
  const unsigned = p.tasks.length > 0 && p.tasks.every((t) => !t.lastAccomplishedAt);

  return html`
    <div class="card">
      <div class="card-h">
        <div>
          <h2>Maintenance programme</h2>
          <p class="tiny dim">
            ${esc(PROGRAMME_BASIS[p.programme.basis] ?? p.programme.basis)} · ${esc(p.programme.revision)}
            ${p.programme.approvedBy ? ` · approved by ${esc(p.programme.approvedBy)}` : ''}
          </p>
        </div>
        <div style="display:flex;gap:6px">
          ${fromManuals}
          <button class="btn sm" data-act="add-task">Add task</button>
          <button class="btn sm" data-act="revise-amp">Revise</button>
          <button class="btn sm" data-act="review-amp">Record review</button>
        </div>
      </div>
      ${unsigned
        ? callout(m.lastInspectionAt ? 'info' : 'caution', m.lastInspectionAt ? 'i' : '!', html`
            <b>Nothing on this programme has been signed off here yet</b>, so every task below is
            measured from
            ${m.lastInspectionAt
              ? html`the last inspection recorded on the aircraft —
                     ${esc(fmtDate(m.lastInspectionAt))}${m.lastInspectionTsn !== null
                       ? ` at ${fmtHours(m.lastInspectionTsn)} h`
                       : ', with no hours recorded against it, so hour-based tasks still count from the current counters'}.`
              : html`the counters as they stand — ${fmtHours(m.tsnHours)} h${m.countersAsOf ? ` on ${esc(fmtDate(m.countersAsOf))}` : ''}.
                     That gives every task a full fresh interval from today, which an aircraft with
                     history behind it has not earned. Record the last inspection under
                     <b>Edit aircraft</b> and these dates move to where they belong.`}
            <br><br>
            Signing a work pack replaces this with real compliance, task by task.
          `)
        : ''}

      <div class="scrollx">
        <table class="tbl">
          <thead>
            <tr>
              <th class="wide">Task</th>
              <th class="col-opt">Source</th>
              <th class="col-opt">Interval</th>
              <th class="col-opt">Last done</th>
              <th>Next due</th>
              <th>Margin</th>
              <th>State</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            ${p.tasks
              .map((t) => {
                const interval = [
                  t.intervalHours ? `${fmtDecimalHours(t.intervalHours)} h` : null,
                  t.intervalMonths ? `${t.intervalMonths} months` : null,
                ].filter(Boolean).join(' / ');
                const tol = t.toleranceHours ? ` (±${fmtDecimalHours(t.toleranceHours)} h)` : t.toleranceDays ? ` (±${t.toleranceDays} d)` : '';
                return html`
                  <tr>
                    <td class="st ${STRIPE[st(t.state)]} wide">
                      ${esc(t.title)}
                      ${t.isPilotOwnerAllowed ? html`<span class="chip" style="margin-left:6px">Pilot-owner</span>` : ''}
                      <span class="sub2">${esc(t.code ?? '')}</span>
                    </td>
                    <td class="col-opt tiny muted">
                      ${esc(SOURCE_LABEL[t.sourceKind] ?? t.sourceKind)}
                      <span class="sub2">${esc(t.sourceReference ?? '')}</span>
                    </td>
                    <td class="n col-opt">${esc(interval || '—')}${esc(tol)}</td>
                    <td class="n col-opt">
                      ${esc(fmtDate(t.lastAccomplishedAt))}
                      ${t.lastAtTsnHours !== null ? html`<span class="sub2">${fmtHours(t.lastAtTsnHours)} h</span>` : ''}
                    </td>
                    <td class="n">
                      ${esc(dueText(t))}
                      ${t.projectedDueAt && t.dueTsnHours !== null ? html`<span class="sub2">≈ ${esc(fmtDate(t.projectedDueAt))}</span>` : ''}
                    </td>
                    <td>
                      ${t.remaining !== null
                        ? arcMeter(t.remaining, t.intervalHours ?? (t.intervalMonths ?? 0) * 30, t.toleranceHours ?? t.toleranceDays ?? 0, t.drivingUnit ?? 'DAYS')
                        : '<span class="dim">—</span>'}
                    </td>
                    <td>${pill(st(t.state), stateLabel(t.state))}</td>
                    <td style="white-space:nowrap"><button class="btn sm" data-task-edit="${esc(t.id)}">Edit</button></td>
                  </tr>
                `;
              })
              .join('')}
          </tbody>
        </table>
      </div>
      <div class="card-f">
        An AMP is approved by the CAMO/CAO managing the aircraft, or declared by the owner — and
        the declaration is only available where no organisation manages it. ML.A.302(c)(9) requires
        an annual effectiveness review.
      </div>
    </div>
  `;
}

// ── weight & balance ────────────────────────────────────────────────────────

function envelopeChart(wb: WeightBalance): string {
  if (wb.envelope.length < 3) return '';
  const W = 520, H = 280, padL = 46, padB = 30, padT = 14, padR = 16;
  const xs = wb.envelope.map((p) => p.cg), ys = wb.envelope.map((p) => p.mass);
  const x0 = Math.min(...xs) - 0.04, x1 = Math.max(...xs) + 0.04;
  const y0 = Math.floor((Math.min(...ys) - 40) / 50) * 50, y1 = Math.ceil((Math.max(...ys) + 40) / 50) * 50;
  const iw = W - padL - padR, ih = H - padB - padT;
  const X = (v: number) => padL + ((v - x0) / (x1 - x0)) * iw;
  const Y = (v: number) => padT + ih - ((v - y0) / (y1 - y0)) * ih;

  let grid = '', labels = '';
  for (let g = y0; g <= y1; g += 100) {
    grid += `<line class="env-grid" x1="${padL}" y1="${Y(g).toFixed(1)}" x2="${W - padR}" y2="${Y(g).toFixed(1)}"/>`;
    labels += `<text class="env-lab" x="${padL - 7}" y="${(Y(g) + 3).toFixed(1)}" text-anchor="end">${g}</text>`;
  }
  for (let g = Math.ceil(x0 * 10) / 10; g <= x1; g += 0.1) {
    grid += `<line class="env-grid" x1="${X(g).toFixed(1)}" y1="${padT}" x2="${X(g).toFixed(1)}" y2="${(padT + ih).toFixed(1)}"/>`;
    labels += `<text class="env-lab" x="${X(g).toFixed(1)}" y="${H - 9}" text-anchor="middle">${g.toFixed(2)}</text>`;
  }
  const poly = wb.envelope.map((p) => `${X(p.cg).toFixed(1)},${Y(p.mass).toFixed(1)}`).join(' ');

  return html`<div class="chart">
    <svg viewBox="0 0 ${W} ${H}" role="img"
         aria-label="Approved centre of gravity envelope">
      ${grid}
      <polygon class="env-poly" points="${poly}"/>
      <text class="env-lab" x="${(padL + iw / 2).toFixed(1)}" y="${H - 0.5}" text-anchor="middle">
        CENTRE OF GRAVITY — METRES AFT OF DATUM
      </text>
    </svg>
  </div>`;
}

function weightBalanceTab(wb: WeightBalance): string {
  if (!wb.report || wb.stations.length === 0) {
    return html`
      <div class="card">
        <div class="card-h"><h2>Weight &amp; balance</h2></div>
        <div class="card-b stack">
          <div class="callout c-info">
            <span class="cg">i</span>
            <div>
              No approved weighing report or loading envelope has been imported for this type, so
              the calculator cannot be offered. Upload the weight &amp; balance report and the
              type's station arms to enable it.
            </div>
          </div>
          <div style="display:flex;gap:8px">
            <button class="btn pri" data-act="record-weighing">Record weighing</button>
            <button class="btn" data-act="define-stations">Define stations</button>
          </div>
        </div>
        <div class="card-f">
          The last weighing report on file is listed under Documents and is used for the
          airworthiness review regardless.
        </div>
      </div>
    `;
  }

  const r = wb.report;
  return html`
    <div class="cols">
      <div class="card">
        <div class="card-h">
          <div>
            <h2>Weighing report</h2>
            <p class="tiny dim">${esc(r.reference)} · ${esc(fmtDate(r.weighedAt))}${r.reason ? ` · ${esc(r.reason)}` : ''}</p>
          </div>
          <div style="display:flex;gap:6px">
            <button class="btn sm" data-act="define-stations">Stations</button>
            <button class="btn pri sm" data-act="record-weighing">Reweigh</button>
          </div>
        </div>
        <div class="kv">
          <div><span class="lbl">Empty mass</span><b>${r.emptyMassKg.toFixed(1)} kg</b><small>as weighed</small></div>
          <div><span class="lbl">Empty arm</span><b>${r.emptyArmMetres.toFixed(3)} m</b><small>aft of datum</small></div>
          <div><span class="lbl">MTOM</span><b>${r.mtomKg.toFixed(0)} kg</b><small>${r.mlmKg ? `MLM ${r.mlmKg.toFixed(0)} kg` : ''}</small></div>
          <div><span class="lbl">Useful load</span><b>${(r.mtomKg - r.emptyMassKg).toFixed(1)} kg</b><small>fuel + payload</small></div>
        </div>
        <div class="scrollx">
          <table class="tbl" style="min-width:0">
            <thead><tr><th class="wide">Station</th><th>Arm (m)</th><th>Max (kg)</th></tr></thead>
            <tbody>
              ${wb.stations
                .map(
                  (s) => html`<tr>
                    <td class="st s-none wide">${esc(s.label)}${s.isFuel ? html`<span class="chip" style="margin-left:6px">Fuel</span>` : ''}</td>
                    <td class="n">${s.armMetres.toFixed(3)}</td>
                    <td class="n">${s.maxMassKg !== null ? s.maxMassKg.toFixed(0) : '—'}</td>
                  </tr>`,
                )
                .join('')}
            </tbody>
          </table>
        </div>
        <div class="card-f">
          Empty mass and arm come from the current weighing report. A new report supersedes them
          and re-runs every saved loading profile.
        </div>
      </div>

      <div class="card">
        <div class="card-h"><h2>Approved envelope</h2><span class="lbl">normal category</span></div>
        <div class="card-b">${envelopeChart(wb)}</div>
        <div class="card-f">
          The interactive loading sheet — enter masses, see the take-off and landing points plotted
          with the burn-off path between them — is the next piece of this tab.
        </div>
      </div>
    </div>
  `;
}

// ── directives ──────────────────────────────────────────────────────────────

function directivesTab(rows: DirectiveRow[]): string {
  const applicable = rows.filter((r) => r.applicable);
  const notApplicable = rows.filter((r) => !r.applicable);

  const row = (d: DirectiveRow) => html`
    <tr class="${d.sourceUrl || d.hasDocument ? 'click' : ''}"
        ${d.sourceUrl || d.hasDocument ? `data-directive-doc="${esc(d.id)}"` : ''}>
      <td class="st ${d.applicable ? STRIPE[st(d.state)] : 's-none'} n">
        ${esc(d.authority)} ${esc(d.reference)}
        <span class="sub2">${esc(d.kind)}${d.hasDocument ? ' · on file' : d.sourceUrl ? ' · published' : ''}</span>
      </td>
      <td class="wide">${esc(d.subject)}</td>
      <td class="n col-opt">
        ${d.isRecurring
          ? d.recurringHours
            ? `every ${fmtDecimalHours(d.recurringHours)} h`
            : d.recurringMonths
              ? `every ${d.recurringMonths} months`
              : 'recurring'
          : 'one-off'}
      </td>
      <td>
        ${d.applicable
          ? html`${pill(st(d.state), d.compliedAt ? (d.isRecurring ? stateLabel(d.state) : 'Complied') : 'Outstanding')}
             <span class="sub2">
               ${d.compliedAt ? `complied ${fmtDate(d.compliedAt)}` : 'no compliance recorded'}${d.nextDueTsnHours !== null ? ` · next at ${fmtHours(d.nextDueTsnHours)} h` : d.nextDueAt ? ` · next ${fmtDate(d.nextDueAt)}` : ''}
             </span>`
          : html`${pill('NA', 'Not applicable')}
             <span class="sub2">${esc(d.justification ?? 'no justification recorded')}</span>`}
      </td>
    </tr>
  `;

  return html`
    <div class="card">
      <div class="card-h">
        <div>
          <h2>Airworthiness directives, service bulletins &amp; standard changes</h2>
          <p class="tiny dim">Applicability decided by rule against type, serial effectivity and fitted components</p>
        </div>
        <div style="display:flex;gap:6px">
          <button class="btn sm" data-act="check-directives">Check online</button>
          <button class="btn sm" data-act="add-directive">Record a directive</button>
          <button class="btn pri sm" data-act="ad-compliance">Record compliance</button>
        </div>
      </div>
      <div class="scrollx">
        <table class="tbl">
          <thead>
            <tr><th>Reference</th><th class="wide">Subject</th><th class="col-opt">Recurrence</th><th>Compliance</th></tr>
          </thead>
          <tbody>${applicable.map(row).join('')}</tbody>
        </table>
      </div>
      <div class="card-f">${fmtInt(applicable.length)} applicable to this aircraft.</div>
    </div>

    ${notApplicable.length > 0
      ? html`<div class="card">
          <div class="card-h">
            <h2>Assessed as not applicable</h2>
            <span class="lbl">${fmtInt(notApplicable.length)} with recorded justification</span>
          </div>
          <div class="scrollx">
            <table class="tbl">
              <thead>
                <tr><th>Reference</th><th class="wide">Subject</th><th class="col-opt">Recurrence</th><th>Assessment</th></tr>
              </thead>
              <tbody>${notApplicable.map(row).join('')}</tbody>
            </table>
          </div>
          <div class="card-f">
            Auditors ask for these as often as for the compliances. Each carries the reason it does
            not apply and the facts the decision was made against.
          </div>
        </div>`
      : ''}
  `;
}

// ── components ──────────────────────────────────────────────────────────────

type Installed = ComponentsResponse['installed'][number];

/**
 * What is left, in whichever unit runs out first.
 *
 * This column used to be hours and nothing else, computed on the page as the
 * limit less the time since overhaul. A component limited only by calendar
 * months or by cycles produced no number, and the cell fell through to "On
 * condition" — the words for a part with no limit at all, printed against a
 * part whose limit was recorded and running. The forecast now answers it, so
 * this column, the due list and the dashboard say the same thing.
 */
function remainingText(r: Installed): string {
  // "12.5 h over", not "-12.5 h": the same words the due list and the tolerance
  // meter use, so a part read here and read there says the same thing.
  return r.life ? fmtMargin(r.life.remaining, r.life.drivingUnit) : 'On condition';
}

/** Where it falls due, in the same unit — the number the margin is counted to. */
function remainingDue(r: Installed): string {
  if (!r.life) return r.shelfLifeExpiry ? `shelf life ${fmtDate(r.shelfLifeExpiry)}` : 'no limit recorded';
  if (r.life.drivingUnit === 'HOURS' && r.life.dueTsnHours !== null) {
    return `at ${fmtHours(r.life.dueTsnHours)} h airframe`;
  }
  if (r.life.drivingUnit === 'CYCLES' && r.life.dueCycles !== null) {
    return `at ${fmtInt(r.life.dueCycles)} cycles`;
  }
  return r.life.dueAt ? fmtDate(r.life.dueAt) : '—';
}

function componentsTab(c: ComponentsResponse): string {
  const lifeState = (r: Installed): State =>
    r.life?.state === 'OVERDUE' ? 'OVERDUE' : r.life?.state === 'DUE_SOON' ? 'DUE_SOON' : 'OK';

  // Records written before a calendar limit required the date it counts from.
  // The date shown for these is counted from the day the part went on the
  // aircraft, which is later than the day it was made or overhauled — so the
  // figure reads generous, and saying so is the only honest thing to do until
  // somebody enters the real date.
  const assumed = c.installed.filter((r) => r.life?.monthsBasis === 'FITMENT');

  return html`
    <div class="card">
      <div class="card-h">
        <div>
          <h2>Installed components</h2>
          <p class="tiny dim">Serialised items with life limits and the release document they were fitted against</p>
        </div>
        <button class="btn pri sm" data-act="fit-component">Fit component</button>
      </div>
      ${assumed.length
        ? html`<div class="card-b" style="padding-bottom:0">
            <div class="callout c-caution">
              <span class="cg">!</span>
              <div>
                <b>${fmtInt(assumed.length)} calendar limit${assumed.length === 1 ? '' : 's'}
                ${assumed.length === 1 ? 'is' : 'are'} counted from the fitment date.</b>
                ${esc(assumed.map((r) => r.description).join(', '))} —
                ${assumed.length === 1 ? 'it has' : 'they have'} no manufacture or overhaul date on
                file, so the months run from the day
                ${assumed.length === 1 ? 'it was' : 'they were'} fitted. That is later than the day
                ${assumed.length === 1 ? 'it was' : 'they were'} made, so the date shown is
                generous. Edit the component to record the real one.
              </div>
            </div>
          </div>`
        : ''}
      <div class="scrollx">
        <table class="tbl">
          <thead>
            <tr>
              <th class="wide">Component</th>
              <th class="col-opt">P/N</th>
              <th>S/N</th>
              <th class="col-opt">Position</th>
              <th class="col-opt">Life</th>
              <th>Remaining</th>
              <th>Release</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            ${c.installed
              .map(
                (r) => html`<tr>
                  <td class="st ${STRIPE[lifeState(r)]} wide">
                    ${esc(r.description)}
                    ${r.easaTerm
                      ? html`<span class="sub2">EASA: ${esc(r.easaTerm)}${r.easaHolder ? ` · ${esc(r.easaHolder)}` : ''}</span>`
                      : ''}
                  </td>
                  <td class="n col-opt">${esc(r.partNumber)}</td>
                  <td class="n">${esc(r.serialNumber ?? '—')}</td>
                  <td class="n col-opt">${esc(r.position)}</td>
                  <td class="tiny muted col-opt">
                    ${esc(LIFE_LABEL[r.lifeKind] ?? r.lifeKind)}
                    ${[
                      r.limitHours ? `${fmtDecimalHours(r.limitHours)} h` : null,
                      r.limitCycles ? `${fmtInt(r.limitCycles)} cyc` : null,
                      r.limitMonths ? `${r.limitMonths} months` : null,
                    ].filter(Boolean).length
                      ? html`<span class="sub2">${esc(
                          [
                            r.limitHours ? `${fmtDecimalHours(r.limitHours)} h` : null,
                            r.limitCycles ? `${fmtInt(r.limitCycles)} cyc` : null,
                            r.limitMonths ? `${r.limitMonths} months` : null,
                          ].filter(Boolean).join(' · '),
                        )}</span>`
                      : ''}
                  </td>
                  <td class="n">
                    ${esc(remainingText(r))}
                    <span class="sub2">${esc(remainingDue(r))}</span>
                    <span class="sub2">TSO ${fmtHours(r.tsoHours)} h</span>
                  </td>
                  <td class="tiny muted">
                    ${r.release ? `${esc(RELEASE_LABEL[r.release.kind] ?? r.release.kind)} · ${esc(r.release.issuingOrg)}` : '—'}
                    ${r.release?.formNumber ? html`<span class="sub2">${esc(r.release.formNumber)}</span>` : ''}
                  </td>
                  <td style="white-space:nowrap">
                    <button class="btn sm" data-component="${esc(r.installationId)}" data-component-act="edit">Edit</button>
                    <button class="btn sm" data-component="${esc(r.installationId)}" data-component-act="remove">Remove</button>
                  </td>
                </tr>`,
              )
              .join('')}
          </tbody>
        </table>
      </div>
      <div class="card-f">
        Release documents travel with the component when it moves between aircraft — EASA Form 1,
        FAA 8130-3 or dual release.
      </div>
    </div>

    ${c.removed.length > 0
      ? html`<div class="card">
          <div class="card-h"><h2>Removal history</h2><span class="lbl">feeds MTBUR on the reliability screen</span></div>
          <div class="scrollx">
            <table class="tbl">
              <thead><tr><th class="wide">Component</th><th>S/N</th><th class="col-opt">Fitted</th><th>Removed</th><th>Reason</th></tr></thead>
              <tbody>
                ${c.removed
                  .map(
                    (r) => html`<tr>
                      <td class="st ${r.removalKind === 'UNSCHEDULED' ? 's-caution' : 's-none'} wide">${esc(r.description)}</td>
                      <td class="n">${esc(r.serialNumber ?? '—')}</td>
                      <td class="n col-opt">${esc(fmtDate(r.fittedAt))}</td>
                      <td class="n">${esc(fmtDate(r.removedAt))}<span class="sub2">${r.removedTsn !== null ? `${fmtHours(r.removedTsn)} h` : ''}</span></td>
                      <td class="tiny muted">${esc(r.removalKind ?? '')}<span class="sub2">${esc(r.removalReason ?? '')}</span></td>
                    </tr>`,
                  )
                  .join('')}
              </tbody>
            </table>
          </div>
        </div>`
      : ''}
  `;
}

const LIFE_LABEL: Record<string, string> = {
  ON_CONDITION: 'On condition',
  HARD_TIME: 'Hard time',
  LIFE_LIMITED: 'Life limited',
  SHELF_LIFE: 'Shelf life',
};

const RELEASE_LABEL: Record<string, string> = {
  EASA_FORM_1: 'EASA Form 1',
  FAA_8130_3: 'FAA 8130-3',
  DUAL_RELEASE: 'Dual release',
  CoC: 'Certificate of conformity',
  SHELF_LIFE_CERT: 'Shelf-life certificate',
};

// ── defects ─────────────────────────────────────────────────────────────────

function defectsTab(rows: DefectRow[]): string {
  const open = rows.filter((d) => d.status === 'OPEN' || d.status === 'DEFERRED');
  const closed = rows.filter((d) => d.status !== 'OPEN' && d.status !== 'DEFERRED');

  const sev = (d: DefectRow): State =>
    d.severity === 'GROUNDING' ? 'OVERDUE' : d.severity === 'DEFERRABLE' ? 'DUE_SOON' : 'OK';

  return html`
    <div class="card">
      <div class="card-h">
        <h2>Defects &amp; deferrals</h2>
        <button class="btn pri sm" data-act="raise-defect">Raise defect</button>
      </div>
      ${open.length === 0
        ? html`<div class="card-b muted">No open defects on this aircraft.</div>`
        : html`<div class="scrollx">
            <table class="tbl">
              <thead><tr><th>Reference</th><th class="wide">Defect</th><th class="col-opt">Raised</th><th>Category</th><th>Limit</th></tr></thead>
              <tbody>
                ${open
                  .map(
                    (d) => html`<tr>
                      <td class="st ${STRIPE[sev(d)]} n">${esc(d.reference)}</td>
                      <td class="wide">${esc(d.description)}${d.recurringGroupId ? html`<span class="sub2">flagged as a repeat defect</span>` : ''}</td>
                      <td class="col-opt tiny muted">${esc(fmtDate(d.raisedAt))}<span class="sub2">${esc(d.raisedByName ?? '')}</span></td>
                      <td><span class="chip">${esc(d.melCategory ? `MEL ${d.melCategory}` : d.severity === 'GROUNDING' ? 'Grounding' : 'Advisory')}</span></td>
                      <td>
                        ${pill(sev(d), d.severity === 'GROUNDING' ? 'No flight' : d.status === 'DEFERRED' ? 'Deferred' : 'Open')}
                        <span class="sub2">${d.deferralLimitAt ? `rectify by ${fmtDate(d.deferralLimitAt)}` : (d.operationalLimitation ?? '')}</span>
                      </td>
                    </tr>`,
                  )
                  .join('')}
              </tbody>
            </table>
          </div>`}
      <div class="card-f">
        A deferral that reaches its limit converts to a grounding item on its own — nobody has to
        remember.
      </div>
    </div>

    ${closed.length > 0
      ? html`<div class="card">
          <div class="card-h"><h2>Rectified</h2><span class="lbl">${fmtInt(closed.length)}</span></div>
          <div class="rows">
            ${closed
              .slice(0, 12)
              .map(
                (d) => html`<div class="row">
                  <div class="bd">
                    <div class="t">${esc(d.reference)} — ${esc(d.description)}</div>
                    <div class="m">raised ${esc(fmtDate(d.raisedAt))}${d.rectifiedAt ? ` · rectified ${fmtDate(d.rectifiedAt)}` : ''}</div>
                  </div>
                </div>`,
              )
              .join('')}
          </div>
        </div>`
      : ''}
  `;
}

// ── tech log ────────────────────────────────────────────────────────────────

function flightsTab(rows: FlightRow[]): string {
  const time = (iso: string | null) => (iso ? new Date(iso).toISOString().slice(11, 16) : '—');
  return html`
    <div class="card">
      <div class="card-h">
        <h2>Tech log</h2>
        <button class="btn pri sm" data-act="record-flight">Record flight</button>
      </div>
      <div class="scrollx">
        <table class="tbl">
          <thead>
            <tr>
              <th>Date</th><th class="col-opt">Sector</th><th>Commander</th>
              <th class="col-opt">Off / on</th><th>Block</th><th>Ldg</th>
              <th class="col-opt">Fuel / oil</th><th class="col-opt">TSN after</th>
            </tr>
          </thead>
          <tbody>
            ${rows.length === 0
              ? html`<tr><td colspan="8" class="muted">No flights recorded.</td></tr>`
              : rows
                  .map(
                    (f) => html`<tr>
                      <td class="st s-none n">${esc(fmtDate(f.flightDate))}</td>
                      <td class="n col-opt">${esc(f.departure ?? '—')} → ${esc(f.arrival ?? '—')}</td>
                      <td>${esc(f.picName ?? '—')}</td>
                      <td class="n col-opt">${esc(time(f.offBlocks))} / ${esc(time(f.onBlocks))}</td>
                      <td class="n">${fmtHours(Number(f.blockHours))} h</td>
                      <td class="n">${f.landings}</td>
                      <td class="n col-opt">${f.fuelUpliftL ? `${Number(f.fuelUpliftL).toFixed(0)} L` : '—'} / ${f.oilUpliftL ? `${Number(f.oilUpliftL).toFixed(1)} L` : '—'}</td>
                      <td class="n col-opt">${f.tsnAfterHours ? `${fmtHours(Number(f.tsnAfterHours))} h` : '—'}</td>
                    </tr>`,
                  )
                  .join('')}
          </tbody>
        </table>
      </div>
      <div class="card-f">
        Entries are immutable once signed; a correction creates an amendment and keeps the original.
      </div>
    </div>
  `;
}

// ── documents ───────────────────────────────────────────────────────────────

function documentsTab(reg: DocumentRegister): string {
  const rows = reg.documents;
  return html`
    <div class="card">
      <div class="card-h">
        <div>
          <h2>Document library</h2>
          <p class="tiny dim">
            ${reg.storage.files} file(s) · ${esc(fmtBytes(reg.storage.bytes))} stored${reg.storage.flagged ? ` · ${reg.storage.flagged} flagged` : ''}
          </p>
        </div>
        <div style="display:flex;gap:6px">
          <button class="btn sm" data-doc="verify">Verify files</button>
          <button class="btn pri sm" data-doc="upload">Add document</button>
        </div>
      </div>
      <div class="scrollx">
        <table class="tbl">
          <thead><tr><th class="wide">Document</th><th class="col-opt">Reference</th><th>Revision held</th><th>Status</th><th>File</th></tr></thead>
          <tbody>
            ${rows
              .map(
                (d) => html`<tr>
                  <td class="st ${d.status === 'CURRENT' ? 's-ok' : 's-caution'} wide">
                    ${docTitle(d)}
                    <span class="sub2">${esc(d.kind)}${d.aircraftId ? '' : ' · organisation-level'}</span>
                  </td>
                  <td class="n col-opt">${esc(d.reference ?? '—')}</td>
                  <td class="n">${esc(d.revision ?? '—')}<span class="sub2">${esc(fmtDate(d.revisionDate))}</span></td>
                  <td>
                    ${pill(
                      d.attachment?.integrityOk === false
                        ? 'OVERDUE'
                        : d.status === 'CURRENT'
                          ? 'OK'
                          : 'DUE_SOON',
                      d.attachment?.integrityOk === false
                        ? 'File problem'
                        : d.status === 'CURRENT'
                          ? 'Current'
                          : 'Superseded',
                    )}
                    <span class="sub2">${d.lastCheckedAt ? `checked ${fmtDate(d.lastCheckedAt)}` : ''}</span>
                  </td>
                  <td style="white-space:nowrap">
                    ${d.attachment
                      ? `<button class="btn sm" data-download="${esc(d.attachment.id)}" data-name="${esc(d.attachment.fileName)}">${esc(fmtBytes(d.attachment.byteSize))}</button>`
                      : '<span class="muted tiny">no file</span>'}
                    ${d.status === 'CURRENT' ? `<button class="btn sm" data-revise="${esc(d.id)}">Revise</button>` : ''}
                    ${d.status === 'CURRENT' ? `<button class="btn sm" data-remove="${esc(d.id)}">Remove</button>` : ''}
                  </td>
                </tr>`,
              )
              .join('')}
          </tbody>
        </table>
      </div>
    </div>
  `;
}

// ── airworthiness review ────────────────────────────────────────────────────

function reviewTab(r: ReviewResponse, ctx: ReviewContext | null): string {
  if (r.regime === 'FAA_91') {
    return html`
      <div class="card">
        <div class="card-h"><h2>Inspection status — FAA registry</h2></div>
        <div class="card-b stack">
          <p class="muted">
            N-registered aircraft carry no ARC. Continuing airworthiness is governed by the
            inspection programme under 14 CFR 91.409 and AD compliance under 91.403(a), released by
            an A&amp;P with Inspection Authorisation.
          </p>
          <div class="kv">
            <div><span class="lbl">Programme</span><b>Annual + 100-hour</b><small>91.409(a)(1) and (b)</small></div>
            <div><span class="lbl">Records</span><b>91.417</b><small>retained and transferable on sale</small></div>
          </div>
        </div>
      </div>
      ${crsCard(r)}
    `;
  }

  const latest = r.reviews[0];
  const steps = latest
    ? [
        ['Records review — continuing airworthiness record set', latest.recordsReviewedAt],
        ['AD and SB status verified against the published state', latest.directivesVerifiedAt],
        [
          `Sample of maintenance records${latest.sampleSize ? ` — ${latest.sampleSize} of ${latest.samplePopulation ?? '?'} CRS entries` : ''}`,
          latest.sampleCheckedAt,
        ],
        ['Physical survey of the aircraft', latest.physicalSurveyAt],
        [`Findings raised and cleared — ${latest.findingsRaised} raised, ${latest.findingsCleared} cleared`, latest.findingsRaised === latest.findingsCleared ? latest.completedAt : null],
      ]
    : [];

  return html`
    <div class="cols">
      <div class="card">
        <div class="card-h">
          <h2>Airworthiness review</h2>
          <div style="display:flex;gap:6px">
            ${ctx && !r.arcs.length ? `<button class="btn sm" data-review="record-arc">Record an existing ARC</button>` : ''}
            ${ctx?.extension.available
              ? '<button class="btn sm" data-review="extend">Extend ARC</button>'
              : ''}
            ${ctx?.openReview
              ? `<button class="btn sm" data-review="steps">Record steps</button>
                 <button class="btn pri sm" data-review="complete">${ctx.planned.recommends ? 'Recommend' : 'Issue certificate'}</button>`
              : ctx && ctx.reviewers.length && ctx.planned.outcome !== 'REFUSED'
                ? '<button class="btn pri sm" data-review="start">Start review</button>'
                : ''}
          </div>
        </div>
        <div class="card-b stack">
          <div class="kv">
            <div>
              <span class="lbl">Current ARC</span>
              <b>${r.arcs[0] ? esc(r.arcs[0].formKind.replace('FORM_', 'Form ').toLowerCase()) : '—'}</b>
              <small>${r.arcs[0] ? `expires ${fmtDate(r.arcs[0].expiresAt)}` : 'none issued'}</small>
            </div>
            <div>
              <span class="lbl">Extensions</span>
              <b>${r.arcs[0]?.extensionNumber ?? 0} of 2</b>
              <small>ML.A.901(c) · one year each</small>
            </div>
            <div>
              <span class="lbl">Privilege</span>
              <b>${r.regime === 'PART_ML' ? 'Direct issue' : 'Recommendation'}</b>
              <small>${r.regime === 'PART_ML' ? 'ML.A.901(b)(2) — Form 15c' : 'M.A.901 — authority issues Form 15a'}</small>
            </div>
            <div>
              <span class="lbl">Blockers</span>
              <b style="color:var(--${r.blockers.canIssue ? 'ok' : 'exceed'})">${r.blockers.canIssue ? 'None' : r.blockers.overdueItems + r.blockers.groundingDefects}</b>
              <small>${r.blockers.overdueItems} overdue · ${r.blockers.groundingDefects} grounding</small>
            </div>
          </div>

          ${latest
            ? html`<div>
                <div class="lbl" style="margin-bottom:7px">Review workflow — ${esc(latest.reference)}</div>
                <div class="rows" style="border:1px solid var(--line);border-radius:var(--radius)">
                  ${steps
                    .map(
                      ([label, done], i) => html`<div class="row" style="border-left-color:var(--${done ? 'ok' : 'line-2'})">
                        <div class="bd">
                          <div class="t">${i + 1} · ${esc(label as string)}</div>
                          <div class="m">${done ? `complete · ${fmtDate(done as string)}` : 'pending'}</div>
                        </div>
                        ${done ? pill('OK', 'Done') : pill('NA', 'Pending')}
                      </div>`,
                    )
                    .join('')}
                  <div class="row" style="border-left-color:var(--${r.blockers.canIssue ? 'line-2' : 'exceed'})">
                    <div class="bd">
                      <div class="t">6 · Issue the certificate and file it with the authority</div>
                      <div class="m">${r.blockers.canIssue ? 'ready when the steps above are complete' : 'blocked while an overdue or grounding item is open'}</div>
                    </div>
                    ${r.blockers.canIssue ? pill('NA', 'Pending') : pill('OVERDUE', 'Blocked')}
                  </div>
                </div>
              </div>`
            : html`<p class="muted">No airworthiness review has been started for this aircraft.</p>`}
        </div>
        <div class="card-f">
          ${ctx && ctx.blockers.length
            ? `Blocked: ${esc(ctx.blockers.join('; '))}.`
            : 'An aircraft with an unresolved grounding item cannot have a certificate issued or extended, so step 6 stays blocked rather than failing at the end.'}
        </div>
      </div>

      <div class="stack">
        <div class="card">
          <div class="card-h"><h2>Certificate history</h2></div>
          <div class="rows">
            ${r.arcs.length === 0
              ? html`<div class="row"><div class="bd"><div class="t">No certificate issued yet</div></div></div>`
              : r.arcs
                  .map(
                    (a) => html`<div class="row" style="border-left-color:var(--${a.revokedAt ? 'exceed' : 'ok'})">
                      <div class="bd">
                        <div class="t">${esc(a.formKind.replace('FORM_', 'Form ').toLowerCase())} · issued ${esc(fmtDate(a.issuedAt))}</div>
                        <div class="m">
                          valid to ${esc(fmtDate(a.expiresAt))}${a.isExtension ? ` · extension ${a.extensionNumber} of 2` : ''}${a.issuedByAuthority ? ` · ${esc(a.issuedByAuthority)}` : ''}
                        </div>
                      </div>
                    </div>`,
                  )
                  .join('')}
          </div>
        </div>
        ${crsCard(r)}
      </div>
    </div>
  `;
}

const CRS_BASIS: Record<string, string> = {
  PART_145: 'Part-145 release',
  ML_A_801_B2: 'Independent certifying staff — ML.A.801(b)(2)',
  ML_A_801_B3_PILOT_OWNER: 'Pilot-owner — ML.A.801(b)(3), Appendix II',
  FAA_43_9: 'FAA 43.9',
  FAA_ANNUAL_IA: 'FAA annual, IA sign-off',
};

/** Opens a certificate in the document viewer, wherever one is listed. */
export function bindCrsPdf(root: ParentNode): void {
  root.querySelectorAll<HTMLElement>('[data-crs-pdf]').forEach((b) =>
    b.addEventListener('click', () =>
      openPdfViewer({
        path: `/planning/crs/${b.dataset.crsPdf}/pdf`,
        title: b.dataset.crsRef ?? 'Certificate of release to service',
        subtitle: 'Certificate of release to service',
        accessToken: getAccessToken,
      }),
    ),
  );
}

const crsCard = (r: ReviewResponse): string => html`
  <div class="card">
    <div class="card-h"><h2>Certificate of release to service</h2></div>
    <div class="rows">
      ${r.crs.length === 0
        ? html`<div class="row"><div class="bd"><div class="t">No CRS recorded</div></div></div>`
        : r.crs
            .map(
              (c) => html`<div class="row">
                <div class="bd">
                  <div class="t">${esc(c.reference)} — ${esc(fmtDate(c.issuedAt))}</div>
                  <div class="m">
                    ${esc(CRS_BASIS[c.basis] ?? c.basis)} · ${esc(c.issuerName)}${c.issuerApproval ? ` · ${esc(c.issuerApproval)}` : ''}${c.atTsnHours !== null ? ` · ${fmtHours(c.atTsnHours)} h` : ''}
                  </div>
                </div>
                <button class="btn sm" data-crs-pdf="${esc(c.id)}" data-crs-ref="${esc(c.reference)}">PDF</button>
              </div>`,
            )
            .join('')}
    </div>
    <div class="card-f">
      Pilot-owner maintenance is restricted to the ML Appendix II task list and is not offered on
      aircraft outside Part-ML.
    </div>
  </div>
`;

// ── orchestration ───────────────────────────────────────────────────────────

/** Overview data is reused by the header on every tab, so it is fetched once. */
const overviewCache = new Map<string, AircraftOverview>();

export async function renderAircraft(host: HTMLElement, id: string, tab: TabKey): Promise<void> {
  let a = overviewCache.get(id);

  if (!a) {
    host.innerHTML = loading('Opening the aircraft record');
    try {
      a = await api.aircraft.overview(id);
      overviewCache.set(id, a);
    } catch (e) {
      host.innerHTML = errorCard(
        e instanceof Error ? e.message : 'Could not open that aircraft.',
        'reload',
      );
      host.querySelector('[data-action=reload]')?.addEventListener('click', () => {
        overviewCache.delete(id);
        void renderAircraft(host, id, tab);
      });
      return;
    }
  }

  // So the helper's chip can say YR-ZEN rather than a cuid. The id is in the
  // URL and the registration is not, and a chip nobody can read is worse than
  // no chip: it is context you cannot check.
  noteRegistration(id, a.registration);

  host.innerHTML = placard(a, tab) + `<div id="tab-body">${loading('Loading')}</div>`;
  host.querySelectorAll<HTMLButtonElement>('[data-tab]').forEach((b) =>
    b.addEventListener('click', () => {
      history.pushState({}, '', `/fleet/${id}/${b.dataset.tab}`);
      void renderAircraft(host, id, b.dataset.tab as TabKey);
    }),
  );

  const body = host.querySelector<HTMLElement>('#tab-body')!;

  try {
    switch (tab) {
      case 'overview': {
        const u = await api.aircraft.utilisation(id);
        body.innerHTML = overviewTab(a, u);
        bindChart(body);
        bindAircraftActions(body, id, () => void renderAircraft(host, id, 'overview'));
        break;
      }
      case 'programme': {
        const p = await api.aircraft.programme(id);
        const again = () => void renderAircraft(host, id, 'programme');
        body.innerHTML = programmeTab(p);
        bindAircraftActions(body, id, again);
        body.querySelectorAll<HTMLElement>('[data-task-edit]').forEach((b) =>
          b.addEventListener('click', () => {
            const task = p.tasks.find((t) => t.id === b.dataset.taskEdit);
            if (task) editProgrammeTask(id, p.programme?.revision ?? '—', task, again);
          }),
        );
        break;
      }
      case 'weight-balance':
        body.innerHTML = weightBalanceTab(await api.aircraft.weightBalance(id));
        bindAircraftActions(body, id, () => void renderAircraft(host, id, 'weight-balance'));
        break;
      case 'directives': {
        const rows = await api.aircraft.directives(id);
        body.innerHTML = directivesTab(rows);
        bindAircraftActions(body, id, () => void renderAircraft(host, id, 'directives'));
        body.querySelectorAll<HTMLElement>('[data-directive-doc]').forEach((tr) =>
          tr.addEventListener('click', (ev) => {
            // The row is clickable, but the buttons inside it are not the row.
            if ((ev.target as HTMLElement).closest('button, a')) return;
            const d = rows.find((x) => x.id === tr.dataset.directiveDoc);
            if (!d) return;
            openPdfViewer({
              path: `/aircraft/${id}/directives/${d.id}/pdf`,
              title: `${d.authority} ${d.reference}`,
              subtitle: d.subject,
              sourceUrl: d.sourceUrl,
              accessToken: getAccessToken,
            });
          }),
        );
        break;
      }
      case 'standard-changes': {
        // The catalogue is fetched alongside, so the dialog can offer the
        // CS-STAN entries rather than asking for a reference to be typed.
        //
        // Two kinds of change on one tab, deliberately: what a standard change
        // is depends on the aircraft being eligible for CS-STAN at all, and
        // everything that is not one — an STC, a major change, a Form 337, a
        // repair — is the same question with a different approval behind it.
        const [changes, catalogue, mods] = await Promise.all([
          api.standardChanges.list(id),
          api.standardChanges.catalogue(id).catch(() => null),
          api.modifications.list(id),
        ]);
        const backToTab = () => void renderAircraft(host, id, 'standard-changes');
        body.innerHTML = modificationsCard(mods) + standardChangesTab(changes);
        bindAircraftActions(body, id, backToTab);
        bindModifications(body, id, mods, backToTab);
        bindStandardChanges(body, id, catalogue?.entries ?? [], backToTab);
        break;
      }
      case 'components': {
        body.innerHTML = componentsTab(await api.aircraft.components(id));
        const reloadComponents = () => void renderAircraft(host, id, 'components');
        bindAircraftActions(body, id, reloadComponents);
        bindComponentActions(body, id, reloadComponents);
        break;
      }
      case 'defects':
        body.innerHTML = defectsTab(await api.aircraft.defects(id));
        bindAircraftActions(body, id, () => void renderAircraft(host, id, 'defects'));
        break;
      case 'flights':
        body.innerHTML = flightsTab(await api.aircraft.flights(id));
        bindAircraftActions(body, id, () => void renderAircraft(host, id, 'flights'));
        break;
      case 'documents': {
        const reg = await api.documents.list(id);
        body.innerHTML = documentsTab(reg);
        bindDocuments(body, reg, { aircraftId: id, registration: a.registration }, () =>
          void renderAircraft(host, id, 'documents'),
        );
        break;
      }
      case 'review':
      {
        // The certificate rules depend on the regime and on what the managing
        // approval is privileged to do, so the workflow needs both payloads.
        const [review, reviewCtx] = await Promise.all([
          api.aircraft.review(id),
          api.aircraft.reviewContext(id).catch(() => null),
        ]);
        body.innerHTML = reviewTab(review, reviewCtx);
        if (reviewCtx) bindReview(body, reviewCtx, () => void renderAircraft(host, id, 'review'));
        bindCrsPdf(body);
      }
        break;
    }
  } catch (e) {
    body.innerHTML = errorCard(e instanceof Error ? e.message : 'Could not load that tab.');
  }
}


/**
 * The review workflow's buttons. Which ones exist is decided by reviewTab from
 * the context, so this only has to route them.
 */
function bindReview(root: HTMLElement, ctx: ReviewContext, reload: () => void): void {
  root.querySelectorAll<HTMLElement>('[data-review]').forEach((btn) =>
    btn.addEventListener('click', () => {
      switch (btn.dataset.review) {
        case 'start':
          return startReview(ctx, reload);
        case 'steps':
          return recordSteps(ctx, reload);
        case 'complete':
          return completeReview(ctx, reload);
        case 'extend':
          return extendArc(ctx, reload);
        case 'record-arc':
          return recordExistingArc(ctx, reload);
      }
    }),
  );
}


/** Upload, revise and verify, from the aircraft's document library. */
function bindDocuments(
  root: HTMLElement,
  reg: DocumentRegister,
  scope: { aircraftId?: string; registration?: string },
  reload: () => void,
): void {
  bindDownloads(root);
  bindDocumentViewers(root);
  root.querySelector('[data-doc="upload"]')?.addEventListener('click', () => uploadDocument(reg, scope, reload));
  root.querySelector('[data-doc="verify"]')?.addEventListener('click', () => void verifyIntegrity());
  root.querySelectorAll<HTMLElement>('[data-revise]').forEach((b) =>
    b.addEventListener('click', () => {
      const doc = reg.documents.find((d) => d.id === b.dataset.revise);
      if (doc) reviseDocument(doc, reload);
    }),
  );
  root.querySelectorAll<HTMLElement>('[data-remove]').forEach((b) =>
    b.addEventListener('click', () => {
      const doc = reg.documents.find((d) => d.id === b.dataset.remove);
      if (doc) removeDocument(doc, reload);
    }),
  );
}


/**
 * The per-tab action buttons.
 *
 * Each opens the same dialog the fleet-wide screen uses, pre-scoped to this
 * aircraft — one implementation, reached from wherever the user happens to be
 * standing.
 */
/**
 * Per-row actions on the components tab.
 *
 * Both need the fit context rather than the row: it carries the installation
 * ids and the positions already in use, which is what the dialogs are built
 * against. Fetched on the click rather than with the tab, so opening Components
 * still costs one query.
 */
function bindComponentActions(root: HTMLElement, aircraftId: string, rerender: () => void): void {
  const reload = () => {
    overviewCache.delete(aircraftId);
    rerender();
  };

  root.querySelectorAll<HTMLElement>('[data-component-act]').forEach((btn) =>
    btn.addEventListener('click', async () => {
      try {
        const ctx = await api.components.context(aircraftId);
        const fitted = ctx.fitted.find((f) => f.installationId === btn.dataset.component);
        if (!fitted) {
          toast('That component is no longer fitted — the list has moved on.', 'error');
          return reload();
        }
        if (btn.dataset.componentAct === 'remove') removeComponent(ctx, fitted, reload);
        else editComponent(ctx, fitted, reload);
      } catch (e) {
        toast(e instanceof Error ? e.message : 'Could not open that.', 'error');
      }
    }),
  );
}

function bindAircraftActions(root: HTMLElement, aircraftId: string, rerender: () => void): void {
  // Everything reached from here writes something. The placard is drawn from a
  // cached overview, so re-rendering without dropping it would redraw the old
  // registration, the old counters and the old fleet state over the new record.
  const reload = () => {
    overviewCache.delete(aircraftId);
    rerender();
  };

  root.querySelectorAll<HTMLElement>('[data-act]').forEach((btn) =>
    btn.addEventListener('click', async () => {
      try {
        switch (btn.dataset.act) {
          case 'fit-component':
            return fitComponent(await api.components.context(aircraftId), reload);
          case 'check-directives': {
            // This one reaches out to the published registers and takes a few
            // seconds, so the button says so rather than appearing dead.
            const label = btn.textContent;
            btn.textContent = 'Checking…';
            (btn as HTMLButtonElement).disabled = true;
            try {
              await runDirectiveCheck(aircraftId, reload);
            } finally {
              btn.textContent = label;
              (btn as HTMLButtonElement).disabled = false;
            }
            return;
          }
          case 'add-directive':
            return addDirective(await api.directives.context(aircraftId), reload);
          case 'ad-compliance':
            return recordCompliance(await api.directives.context(aircraftId), null, reload);
          case 'raise-defect':
            return raiseDefectFor(aircraftId, reload);
          case 'amp-from-manuals': {
            // Its own screen, not a dialog: reviewing two hundred tasks
            // against the manual is not modal work.
            history.pushState({}, '', `/fleet/${aircraftId}/amp`);
            window.dispatchEvent(new PopStateEvent('popstate'));
            return;
          }
          case 'add-task': {
            const ctx = await api.programme.context(aircraftId);
            return editProgrammeTask(aircraftId, ctx.current?.revision ?? '—', null, reload);
          }
          case 'revise-amp':
            return reviseProgramme(await api.programme.context(aircraftId), reload);
          case 'review-amp':
            return recordProgrammeReview(await api.programme.context(aircraftId), reload);
          case 'record-weighing':
            return recordWeighing(await api.weightBalance.context(aircraftId), reload);
          case 'define-stations':
            return defineStations(await api.weightBalance.context(aircraftId), reload);
          case 'records-transfer':
            return showRecordsTransfer(await api.reports.recordsTransfer(aircraftId));
          case 'edit-aircraft':
            return editAircraft(
              await api.aircraft.overview(aircraftId),
              await api.aircraft.context(),
              reload,
            );
          case 'end-management':
            return endManagement(await api.aircraft.endOfManagement(aircraftId), reload);
          case 'restore-aircraft': {
            const restored = await api.aircraft.restore(aircraftId);
            toast(`${restored.registration} is back on the fleet — everything due is recomputed on the next flight or work pack`);
            return reload();
          }
          case 'record-flight':
            // The tech log is a screen rather than a dialog: filing an entry
            // needs the aircraft's live counters and the consequence preview,
            // and duplicating that here would be a second implementation of
            // the one write that must not go wrong.
            history.pushState({}, '', '/techlog');
            dispatchEvent(new PopStateEvent('popstate'));
            return;
        }
      } catch (e) {
        toast(e instanceof Error ? e.message : 'Could not open that.', 'error');
      }
    }),
  );
}
